import { CommonModule } from '@angular/common';
import { Component, inject, signal, viewChild } from '@angular/core';
import type { ElementRef, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { WebrtcStreamService } from '@hunting-nature/Secacam/shared/services/webrtc-stream.service';
import { AppButtonComponent } from '@hunting-nature/shared-ui/components/app-button/app-button.component';
import { LanguageSelectorComponent } from '@hunting-nature/shared-ui/components/language-selector/language-selector.component';
import { IfNativeDirective } from '@hunting-nature/shared-ui/directives/if-native.directive';
import { FormatStorageSizePipe } from '@hunting-nature/shared-ui/pipes/format-storage-size.pipe';
import type { ScrollDetail, ToggleChangeEventDetail, ViewDidEnter } from '@ionic/angular/standalone';
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonLoading,
  IonNote,
  IonText,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { addIcons } from 'ionicons';
import { chevronBackOutline, informationCircleOutline } from 'ionicons/icons';

import { environment } from '../../../../environments/environment';
import { MenuButton } from '../../../shared/components/menu-button/menu-button';
import { OfflineBannerComponent } from '../../../shared/components/offline-banner/offline-banner.component';
import { GeneralActions } from '../../../shared/redux/general/general.actions';
import { NotificationSettingsActions } from '../notification-settings/redux/notification-settings.action';
import { GeneralSelectors } from '../../../shared/redux/general/general.reducer';
import { SettingsActions } from '../../../shared/redux/settings/settings.actions';
import { SettingsSelectors } from '../../../shared/redux/settings/settings.reducer';
import { AccountCard } from '../account-card/account-card';
import { ContactsCard } from '../contacts-card/contacts-card';
import { ResetApplicationComponent } from '../device-settings/storage-management/components/reset-application/reset-application.component';
import { ConsentActions } from '../consent-modal/redux/consent.actions';
import { MenuActions } from '../redux/menu.action';
import { MenuSelectors } from '../redux/menu.reducer';
import { ShopCard } from '../shop-card/shop-card';
import { MenuSubscriptionCard } from '@hunting-nature/Secacam/pages/menu/menu-subscription-card/menu-subscription-card';
import { HuntingProductsActions } from '../../hunting-products/redux/hunting-products.actions';
import { HuntingProductsSelectors } from '../../hunting-products/redux/hunting-products.reducer';

@Component({
  selector: 'app-menu-root',
  imports: [
    IonLoading,
    IonText,
    IonListHeader,
    IonLabel,
    IonItem,
    IonList,
    AppButtonComponent,
    TranslocoPipe,
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    CommonModule,
    IonToggle,
    LanguageSelectorComponent,
    RouterModule,
    MenuSubscriptionCard,
    AccountCard,
    ContactsCard,
    IonLoading,
    IonButtons,
    ShopCard,
    MenuButton,
    ResetApplicationComponent,
    IonNote,
    FormatStorageSizePipe,
    IonIcon,
    IfNativeDirective,
    OfflineBannerComponent,
  ],
  templateUrl: './menu-root.component.html',
  styleUrls: ['./menu-root.component.scss'],
})
export class MenuRootComponent implements ViewDidEnter, OnDestroy {
  // Animation constants
  private static readonly INITIAL_TOOLBAR_OPACITY = 0.92;
  private static readonly TOOLBAR_OPACITY_RANGE = 0.08; // 0.92 → 1.0
  private static readonly PERCENTAGE_MULTIPLIER = 100;
  private static readonly SHADOW_BLUR_RADIUS = '8px';

  // ViewChild for measuring actual header height
  private readonly headerElement = viewChild<ElementRef<HTMLElement>>('headerElement');

  // services
  private readonly store = inject(Store);
  private readonly webrtcStreamService = inject(WebrtcStreamService);

  // UI bindings
  protected readonly isTestSectionEnabled = FEATURE_TEST_SECTION;
  protected readonly isMockHuntingDeviceEnabled = FEATURE_MOCK_HUNTING_DEVICE;
  protected readonly version = environment.version;
  protected readonly loggingOut$$ = this.store.selectSignal(GeneralSelectors.loggingOut);
  protected readonly userEMail$$ = this.store.selectSignal(GeneralSelectors.userEMail);
  protected readonly isUseDarkMode$$ = this.store.selectSignal(GeneralSelectors.isUseDarkMode);
  protected readonly activeAppLanguage$$ = this.store.selectSignal(GeneralSelectors.activeAppLanguage);
  protected readonly availableAppLanguages$$ = this.store.selectSignal(GeneralSelectors.availableAppLanguages);
  protected readonly isConsentLoading$$ = this.store.selectSignal(SettingsSelectors.isConsentLoading);
  protected readonly freeDeviceSpace$$ = this.store.selectSignal(MenuSelectors.getFreeDeviceSpace);
  protected readonly isCalculatingFreeDeviceSpace$$ = this.store.selectSignal(MenuSelectors.isCalculatingFreeDeviceSpace);
  protected readonly isStorageInformationShown$$ = this.store.selectSignal(MenuSelectors.isStorageInformationShown);
  protected readonly hasContacts$$ = this.store.selectSignal(MenuSelectors.hasContacts);
  protected readonly isMockDeviceConnected$$ = this.store.selectSignal(HuntingProductsSelectors.isConnected);
  protected readonly isStreamStatsEnabled$$ = this.webrtcStreamService.isStatsEnabled$$;

  // Background image overlay opacity (0 = fully visible background, 1 = fully covered)
  protected readonly backgroundOverlayOpacity$$ = signal(0);
  // Toolbar opacity (0.92 at top for contrast, 1.0 when scrolled)
  protected readonly toolbarOpacity$$ = signal(MenuRootComponent.INITIAL_TOOLBAR_OPACITY);
  // Title color (white at top over hero image, transitions to theme color as background fades)
  protected readonly titleColor$$ = signal('#fff'); // Will be reset from CSS in ionViewDidEnter
  // Title text-shadow (dark when white text, background-color when dark text)
  protected readonly titleTextShadow$$ = signal(''); // Will be computed in ionViewDidEnter
  // Icon drop-shadow (same as title text-shadow but using filter syntax)
  protected readonly iconDropShadow$$ = signal(''); // Will be computed in ionViewDidEnter
  // Gradient opacity (1 at top for contrast, 0 when scrolled - inverse of overlay)
  protected readonly gradientOpacity$$ = signal(1);
  // Background image loaded state
  protected readonly backgroundImageLoaded$$ = signal(false);
  private fadeEndScroll = 350; // Cached to avoid getComputedStyle on every scroll
  private rafId: number | null = null; // RequestAnimationFrame ID for throttling

  constructor() {
    addIcons({
      openExternally: 'assets/icons/open-in-new-icon.svg',
      chevronBackOutline,
      informationCircleOutline,
    });
  }

  // life cycle method
  ionViewDidEnter() {
    this.store.dispatch(MenuActions.menuEntered({ language: this.activeAppLanguage$$() ?? 'en', contactsAlreadyLoaded: this.hasContacts$$() }));
    this.resetAnimationState();
    this.updateHeaderHeight();
    this.calculateFadeThreshold();
  }

  ngOnDestroy(): void {
    // Cancel pending RAF to prevent memory leaks
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // Helper methods
  private resetAnimationState(): void {
    // Get initial values from CSS/theme for consistency
    // Use --app-hero-overlay-title-color (white in both themes) for text over hero image
    const initialTitleColor = getComputedStyle(document.documentElement).getPropertyValue('--app-hero-overlay-title-color').trim() || '#fff';
    const initialShadowColor = 'rgba(0, 0, 0, 0.8)';
    const blur = MenuRootComponent.SHADOW_BLUR_RADIUS;

    this.backgroundOverlayOpacity$$.set(0);
    this.toolbarOpacity$$.set(MenuRootComponent.INITIAL_TOOLBAR_OPACITY);
    this.titleColor$$.set(initialTitleColor);
    this.titleTextShadow$$.set(`0 0 ${blur} ${initialShadowColor}`);
    this.iconDropShadow$$.set(`drop-shadow(0 0 ${blur} ${initialShadowColor})`);
    this.gradientOpacity$$.set(1);
  }

  private updateHeaderHeight(): void {
    // Measure actual rendered header height and update CSS variable
    // This ensures all calculations (SCSS padding + TS fade threshold) use the real height
    const headerElement = this.headerElement()?.nativeElement;
    if (headerElement) {
      const headerHeight = headerElement.getBoundingClientRect().height;
      // Set CSS variable so SCSS can reference it for padding calculations
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }
  }

  private calculateFadeThreshold(): void {
    // Calculate fade threshold: hero background height minus header height
    // Animation completes when content heading reaches bottom of header
    const heroHeight = this.getCssVariableAsNumber('--hero-background-height', 350);
    const safeAreaTop = this.getCssVariableAsNumber('--ion-safe-area-top', 0);
    const headerHeight = this.getCssVariableAsNumber('--header-height', 56);

    // Fade should complete when scrolled by: heroHeight - (safeAreaTop + headerHeight)
    // This is when the first content item reaches the bottom of the header
    this.fadeEndScroll = heroHeight - (safeAreaTop + headerHeight);
  }

  private getCssVariableAsNumber(variableName: string, fallback: number): number {
    const value = getComputedStyle(document.documentElement).getPropertyValue(variableName);
    return Number.parseInt(value) || fallback;
  }

  private interpolateShadowColor(scrollProgress: number): string {
    const inverseProgress = (1 - scrollProgress) * MenuRootComponent.PERCENTAGE_MULTIPLIER;
    const progress = scrollProgress * MenuRootComponent.PERCENTAGE_MULTIPLIER;
    return `color-mix(in srgb, rgba(0, 0, 0, 0.8) ${inverseProgress}%, var(--app-content-background) ${progress}%)`;
  }

  // UI handlers
  handleScroll(event: CustomEvent<ScrollDetail>): void {
    // Cancel pending RAF to avoid queueing multiple updates
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    // Use RAF to sync with browser paint for smooth updates
    this.rafId = requestAnimationFrame(() => {
      const scrollTop = event.detail.scrollTop;

      // Linear fade (using cached threshold)
      const scrollProgress = Math.max(0, Math.min(scrollTop / this.fadeEndScroll, 1));
      const inverseProgress = 1 - scrollProgress;
      const percentage = MenuRootComponent.PERCENTAGE_MULTIPLIER;

      // Background overlay: 0 → 1
      this.backgroundOverlayOpacity$$.set(scrollProgress);

      // Gradient opacity: 1 → 0 (inverse of overlay)
      this.gradientOpacity$$.set(inverseProgress);

      // Toolbar opacity: 0.92 → 1.0
      const toolbarOpacity = MenuRootComponent.INITIAL_TOOLBAR_OPACITY + scrollProgress * MenuRootComponent.TOOLBAR_OPACITY_RANGE;
      this.toolbarOpacity$$.set(toolbarOpacity);

      // Title color: hero overlay color (white) → toolbar color
      const initialColor = getComputedStyle(document.documentElement).getPropertyValue('--app-hero-overlay-title-color').trim() || '#fff';
      const titleColor = `color-mix(in srgb, ${initialColor} ${inverseProgress * percentage}%, var(--ion-toolbar-color) ${scrollProgress * percentage}%)`;
      this.titleColor$$.set(titleColor);

      // Title text-shadow and icon drop-shadow use same color interpolation
      const shadowColor = this.interpolateShadowColor(scrollProgress);
      const blur = MenuRootComponent.SHADOW_BLUR_RADIUS;
      this.titleTextShadow$$.set(`0 0 ${blur} ${shadowColor}`);
      this.iconDropShadow$$.set(`drop-shadow(0 0 ${blur} ${shadowColor})`);

      this.rafId = null;
    });
  }

  onBackgroundImageLoaded(): void {
    this.backgroundImageLoaded$$.set(true);
  }

  handleOpenStorageManagement(): void {
    this.store.dispatch(GeneralActions.goToMenuStorageManagement());
  }

  handleLogout(): void {
    this.store.dispatch(GeneralActions.logout());
  }

  handleToggleDarkMode(event: CustomEvent<ToggleChangeEventDetail<{ checked: boolean }>>): void {
    this.store.dispatch(GeneralActions.useDarkMode({ darkMode: event.detail.checked }));
  }

  handleChangeLanguage(language: string): void {
    this.store.dispatch(GeneralActions.changeAppLanguage({ language }));
  }

  handleShowAnalysisTests(): void {
    this.store.dispatch(GeneralActions.goToShotAnalysisTests());
  }

  handleOpenTermsAndConditions(): void {
    this.store.dispatch(SettingsActions.showTermsAndConditions());
  }

  handleOpenDataPrivacy(): void {
    this.store.dispatch(SettingsActions.showDataPrivacy());
  }

  handleOpenDataPrivacyAdditionAI(): void {
    this.store.dispatch(SettingsActions.showDataPrivacyAdditionAI());
  }

  handleNotYetImplemented(): void {
    void this.store.dispatch(GeneralActions.openComingSoonModal());
  }

  handleOpenNotificationSettings(): void {
    this.store.dispatch(NotificationSettingsActions.goToMenuNotificationSettings());
  }

  handleOpenAppSettings(): void {
    this.store.dispatch(MenuActions.openAppSettings());
  }

  handleTokenTests(): void {
    this.store.dispatch(GeneralActions.goToTokenTests());
  }

  handleWebRTCTest(): void {
    this.store.dispatch(GeneralActions.goToWebRTCTest());
  }

  handleToggleStreamStats(event: CustomEvent<ToggleChangeEventDetail<{ checked: boolean }>>): void {
    this.webrtcStreamService.setStatsEnabled(event.detail.checked);
  }

  handleToggleMockHuntingDevice(event: CustomEvent<ToggleChangeEventDetail<{ checked: boolean }>>): void {
    this.store.dispatch(event.detail.checked ? HuntingProductsActions.activateMockHuntingDevice() : HuntingProductsActions.deactivateMockHuntingDevice());
  }

  handleOpenFaq(): void {
    this.store.dispatch(MenuActions.openFaq());
  }

  handleOpenContactSupport(): void {
    this.store.dispatch(MenuActions.openContactSupport());
  }

  handleOpenConsentModal(): void {
    this.store.dispatch(ConsentActions.openConsentModal({ language: this.activeAppLanguage$$() ?? 'en' }));
  }
}
